import { useMemo } from "react";
import { useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useIonRouter } from "@ionic/react";
import { toast } from "sonner";
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Clock3,
  Trophy,
  Zap,
} from "lucide-react";
import { ProgramDataProxy } from "@entities/program/api/data.proxy";
import { type ProgramEnrollment } from "@entities/program/types/template";
import { getDaysLeftAmount } from "@entities/program/lib/daysLeftAmount";
import {
  getProgramDayDate,
  getProgramDayDateLabel,
  getProgramWeekDateRangeLabel,
  getProgramWeekStartDate,
} from "@entities/program/lib/getWeekDates";
import { getWeeksAmount } from "@entities/program/lib/weeksAmount";
import { getMediaUrl } from "@shared/helpers/getMediaUrl";
import { DefaultPage } from "@shared/layout/DefaultPage";
import { CustomLoader } from "@shared/animations/loaders/CustomLoader";
import DefaultProgramPic from "@assets/images/DefaultProgramPic.png";
import { Day, Week, WeekStatus } from "@ui/VerticalStepper/types";
import {
  ProgramCompactHeader,
  ProgramCompactHeaderSentinel,
  useProgramCompactHeaderVisibility,
} from "@pages/program/ui/ProgramCompactHeader";

const ENROLLMENT_QUERY_STALE_TIME_MS = 20 * 10 * 1000;
const FUTURE_WEEK_MESSAGE = "Не торопитесь, сначала закончите текущую неделю";

type CalendarDay = Day & {
  dateLabel: string;
};

type CalendarWeek = Omit<Week, "days"> & {
  dateRangeLabel: string;
  days: CalendarDay[];
};

const isWeekCompleted = (
  planWeek: ProgramEnrollment["plan"]["weeks"][string] | undefined,
  progressWeek: ProgramEnrollment["progress"]["weeks"][string] | undefined,
): boolean => {
  if (!planWeek || !progressWeek) {
    return false;
  }

  const planDays = Object.entries(planWeek.days).filter(([, day]) =>
    Boolean(day?.workout_id),
  );
  if (planDays.length === 0) {
    return false;
  }

  return planDays.every(([dayKey]) => {
    const progressDay = progressWeek.days[dayKey];
    return (
      progressDay?.status === "completed" || progressDay?.status === "skipped"
    );
  });
};

const getWeekStatus = (
  weekNumber: number,
  weekPointer: number,
  isLast: boolean,
): WeekStatus => {
  if (weekPointer > weekNumber) {
    return "finished";
  }

  if (weekPointer === weekNumber) {
    return "notFinished";
  }

  return isLast ? "last" : "notStarted";
};

const getWeekTitleColor = (status: WeekStatus) => {
  if (status === "finished") {
    return "text-[#2F3437]";
  }

  if (status === "notFinished") {
    return "text-[#111111]";
  }

  return "text-[#8D8F8A]";
};

const getWeekMarkerTone = (status: WeekStatus) => {
  if (status === "finished") {
    return "bg-[#7F86D3] text-white shadow-[0_10px_24px_rgba(127,134,211,0.22)]";
  }

  if (status === "notFinished") {
    return "bg-[#DDFB1F] text-[#111111] shadow-[0_10px_24px_rgba(221,251,31,0.26)]";
  }

  return "bg-[#E1E2DD] text-white";
};

const getDayTone = (dayType: Day["type"], weekStatus: WeekStatus) => {
  if (weekStatus === "notStarted" || weekStatus === "last") {
    return "bg-[#E3E4E0] text-white";
  }

  if (dayType === "current") {
    return "bg-[#DDFB1F] text-[#111111] shadow-[0_10px_18px_rgba(221,251,31,0.2)]";
  }

  if (dayType === "finished") {
    return "bg-[#7F86D3] text-white";
  }

  if (dayType === "skipped") {
    return "bg-[#E1E2DD] text-[#666A63]";
  }

  if (dayType === "training") {
    return "bg-[#EEE7FF] text-[#111111]";
  }

  if (dayType === "rest") {
    return "bg-[#F0F0EA] text-[#7A7A74]";
  }

  return "bg-[#E1E2DD] text-white";
};

const getWeekCaption = (status: WeekStatus, completedDays: number) => {
  if (status === "finished") {
    return "Неделя завершена";
  }

  if (status === "notFinished") {
    return completedDays > 0 ? "Продолжай текущую неделю" : "Текущая неделя";
  }

  return "Откроется по ходу программы";
};

const getWeekBadgeLabel = (status: WeekStatus, completedDays: number) => {
  if (status === "finished") {
    return "Завершена";
  }

  if (status === "notFinished") {
    return completedDays > 0 ? "В процессе" : "Текущая";
  }

  return "Закрыта";
};

const getWeekBadgeTone = (status: WeekStatus) => {
  if (status === "finished") {
    return "bg-[#EEF0FF] text-[#646CC7]";
  }

  if (status === "notFinished") {
    return "bg-[#DDFB1F] text-[#111111]";
  }

  return "bg-[#ECEDEA] text-[#8D8F8A]";
};

export const ProgramEnrollmentCalendarPage = () => {
  const { enrollmentId } = useParams<{ enrollmentId: string }>();
  const nav = useIonRouter();
  const {
    isCompactHeaderVisible,
    compactHeaderRootRef,
    compactHeaderSentinelRef,
  } = useProgramCompactHeaderVisibility();

  const { data: program } = useQuery({
    queryKey: ["enrollment", enrollmentId],
    queryFn: async () => {
      return ProgramDataProxy.getEnrollmentById(+enrollmentId);
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    staleTime: ENROLLMENT_QUERY_STALE_TIME_MS,
  });

  const weeks = useMemo<CalendarWeek[]>(() => {
    if (!program) {
      return [];
    }

    const totalProgramWeeks = Object.keys(program.plan?.weeks ?? {}).length;
    const isFinished = (program.statistics?.progress_percentage ?? 0) >= 100;
    const calendarWeeks: CalendarWeek[] = [];

    const pointerWeekKey = `${program.pointer_week}`;
    const pointerWeekProgress = program.progress?.weeks?.[pointerWeekKey];
    const pointerWeekDays = pointerWeekProgress?.days ?? {};
    let firstPendingInPointerWeek = program.pointer_day;
    for (let d = 1; d <= 7; d++) {
      const day = pointerWeekDays[`${d}`];
      if (day && day.status !== "completed" && day.status !== "skipped") {
        firstPendingInPointerWeek = d;
        break;
      }
    }

    for (let weekIndex = 1; weekIndex <= totalProgramWeeks; weekIndex++) {
      const weekKey = `${weekIndex}`;
      const planWeek = program.plan.weeks[weekKey];
      const progressWeek = program.progress.weeks[weekKey];
      const days: CalendarDay[] = [];
      let completedDays = 0;
      const weekCompleted = isWeekCompleted(planWeek, progressWeek);
      const isFutureWeek = !isFinished && weekIndex > program.pointer_week;
      const computedWeekStartAt =
        progressWeek?.week_start_at ??
        getProgramWeekStartDate(program.started_at, weekIndex)?.toISOString();

      for (let dayIndex = 1; dayIndex <= 7; dayIndex++) {
        const dayKey = `${dayIndex}`;
        const planDay = planWeek?.days[dayKey];
        const progressDay = progressWeek?.days[dayKey];
        const hasWorkout = Boolean(
          planDay?.workout_id ?? progressDay?.workout_id,
        );
        const isPastDay =
          weekIndex < program.pointer_week ||
          (weekIndex === program.pointer_week &&
            dayIndex < firstPendingInPointerWeek);
        const isSkippedWorkout =
          hasWorkout && progressDay?.status === "skipped";
        const isHistoricalWorkout =
          hasWorkout &&
          (progressDay?.status === "completed" ||
            isSkippedWorkout ||
            isPastDay);
        const workoutHistoryId =
          progressDay?.workout_history_id ??
          progressDay?.workout?.history?.id_workout_history;
        const dayDate = getProgramDayDate({
          dayNum: dayIndex,
          weekStartAt: computedWeekStartAt,
        });
        let dayType: Day["type"] = "inactive";

        if (isSkippedWorkout) {
          dayType = "skipped";
          completedDays += 1;
        } else if (isHistoricalWorkout) {
          dayType = "finished";
          completedDays += 1;
        } else if (
          weekIndex === program.pointer_week &&
          dayIndex === firstPendingInPointerWeek
        ) {
          dayType = "current";
        } else if (hasWorkout) {
          dayType = "training";
        } else {
          dayType = "rest";
        }

        days.push({
          id: `${planDay?.workout_id ?? progressDay?.workout_id ?? `id_${weekIndex}_${dayIndex}`}`,
          type: dayType,
          dateLabel: getProgramDayDateLabel(dayDate),
          item: {
            title: `${dayIndex}`,
            onClick: () => {
              if (isFutureWeek) {
                toast.message(FUTURE_WEEK_MESSAGE, {
                  position: "top-center",
                });
                return;
              }

              if (isHistoricalWorkout) {
                const historyUrl = `/program/${enrollmentId}/week/${weekIndex}/day/${dayIndex}/history`;
                nav.push(
                  workoutHistoryId
                    ? `${historyUrl}?workoutHistoryId=${workoutHistoryId}`
                    : historyUrl,
                );
                return;
              }

              nav.push(
                `/program/${enrollmentId}/week/${weekIndex}?day=${dayIndex}`,
              );
            },
          },
        });
      }

      calendarWeeks.push({
        title: `Неделя ${weekIndex}`,
        dateRangeLabel: getProgramWeekDateRangeLabel(computedWeekStartAt),
        isCompleted: weekCompleted,
        overallDays: 7,
        completedDays: weekCompleted ? 7 : completedDays,
        days,
      });
    }

    return calendarWeeks;
  }, [enrollmentId, nav, program]);

  if (!program) {
    return (
      <DefaultPage noPadding style={{ background: "#F8F8F3" }}>
        <main className="flex h-full w-full items-center justify-center bg-[#F8F8F3]">
          <CustomLoader />
        </main>
      </DefaultPage>
    );
  }

  const totalWeeks = Object.keys(program.plan?.weeks ?? {}).length;
  const weeksAmount = getWeeksAmount(program);
  const daysLeft = getDaysLeftAmount(
    weeksAmount,
    program.pointer_week,
    program.pointer_day,
  );
  const progressPercentage = Math.min(
    Math.max(program.statistics?.progress_percentage ?? 0, 0),
    100,
  );
  const progressBorderClipRight = `${100 - progressPercentage}%`;
  const heroImage = getMediaUrl(program.media?.cover) || DefaultProgramPic;
  const programTitle = program.program_name || "Программа тренировок";
  const programFormat = `7x${weeksAmount || totalWeeks || 1}`;
  const programDescription = `Программа ${programFormat}, день ${program.pointer_day}`;
  const isProgramFinished = progressPercentage >= 100;
  const finishCaption = isProgramFinished
    ? "Все недели закрыты"
    : daysLeft > 0
      ? `Осталось ${daysLeft} дней`
      : "Финиш уже рядом";

  return (
    <DefaultPage noPadding style={{ background: "#F8F8F3" }}>
      <main className="h-full w-full max-w-full overflow-hidden bg-[#F8F8F3] text-[#111111]">
        <section className="relative flex h-full flex-col overflow-hidden">
          <ProgramCompactHeader
            isVisible={isCompactHeaderVisible}
            onBack={() => nav.push(`/program/${enrollmentId}`)}
            title={programTitle}
            rightLabel={programFormat}
            details={
              <>
                <span className="min-w-0 truncate">{programDescription}</span>
                <span className="size-1 shrink-0 rounded-full bg-[#C8CCC1]" />
                <span className="shrink-0">{progressPercentage}%</span>
              </>
            }
          />

          <div
            className="h-full overflow-y-auto overscroll-contain pb-[calc(32px+env(safe-area-inset-bottom))] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            ref={compactHeaderRootRef}
          >
            <div className="relative">
              <ProgramCompactHeaderSentinel ref={compactHeaderSentinelRef} />
              <div className="relative z-0 h-[336px] overflow-hidden bg-[#F7F8F4]">
                <div className="absolute inset-0 overflow-hidden">
                  <div
                    className="absolute bottom-0 left-[38%] right-[-42%] top-0 overflow-hidden rounded-bl-[72px]"
                    style={{
                      WebkitMaskImage:
                        "linear-gradient(112deg, transparent 0%, transparent 18%, black 48%, black 100%)",
                      maskImage:
                        "linear-gradient(112deg, transparent 0%, transparent 18%, black 48%, black 100%)",
                    }}
                  >
                    <img
                      src={heroImage}
                      alt=""
                      className="h-full w-full object-cover object-[90%_center] opacity-100"
                    />
                  </div>
                  <div className="absolute inset-x-0 bottom-0 h-[24%] bg-[linear-gradient(180deg,rgba(247,248,244,0)_0%,rgba(247,248,244,0.68)_100%)]" />
                </div>
                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.04)_0%,rgba(248,248,243,0.02)_50%,rgba(248,248,243,0.42)_100%)]" />
                <div className="absolute left-5 top-[calc(22px+env(safe-area-inset-top))] z-30 flex flex-col items-start">
                  <button
                    type="button"
                    onClick={() => nav.push(`/program/${enrollmentId}`)}
                    aria-label="Назад"
                    className="flex size-[48px] items-center justify-center overflow-hidden rounded-[18px] bg-[#F0F1EF] p-[3px] text-[#111111] ring-1 ring-black/[0.035] transition-transform duration-150 ease-out active:scale-[0.96]"
                  >
                    <ArrowLeft
                      size={25}
                      strokeWidth={2.45}
                      className="shrink-0"
                    />
                  </button>
                  <div className="mt-2 inline-flex h-8 items-center rounded-[12px] bg-[#DDFB1F] px-3 text-[14px] font-bold text-[#111111] shadow-[0_10px_24px_rgba(221,251,31,0.22)]">
                    {programFormat}
                  </div>
                </div>
                <div className="absolute inset-x-0 bottom-0 z-20 px-5 pb-14">
                  <h1 className="line-clamp-2 max-w-[350px] text-[34px] font-extrabold leading-[1.02] tracking-[0] text-[#111111]">
                    {programTitle}
                  </h1>
                  <p className="mt-2 max-w-[310px] text-[16px] font-medium leading-[1.25] text-[#30302C]">
                    {programDescription}
                  </p>
                  <div className="mt-4 flex items-center gap-2 text-[14px] font-medium text-[#252525]">
                    <span className="inline-flex items-center gap-2">
                      <Clock3 size={17} strokeWidth={2.1} />
                      Осталось {daysLeft} дней
                    </span>
                  </div>
                </div>
              </div>

              <div className="relative z-10 -mt-10 rounded-t-[34px] bg-[#F8F8F3] px-5 pt-[24px] shadow-[0_-14px_34px_rgba(20,20,20,0.045)]">
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 top-0 z-30 h-[34px] overflow-hidden rounded-t-[34px]"
                >
                  <div className="absolute inset-x-0 top-0 h-[34px] rounded-t-[34px] border-x border-t border-[#DADDE0]/80" />
                  <div
                    className="absolute inset-x-0 top-0 h-[34px] rounded-t-[34px] border-x-[3px] border-t-[3px] border-[#D7FB36]"
                    style={{
                      clipPath: `inset(0 ${progressBorderClipRight} 0 0)`,
                    }}
                  />
                </div>
                <div className="absolute right-5 top-[-30px] z-20 text-[14px] font-bold text-[#111111]/55">
                  {progressPercentage}%
                </div>
                <div className="flex flex-col gap-3.5 pb-[calc(32px+env(safe-area-inset-bottom))]">
                  {weeks.map((week, index) => {
                    const weekNumber = index + 1;
                    const status = getWeekStatus(
                      weekNumber,
                      program.pointer_week,
                      index === weeks.length - 1,
                    );
                    const weekProgress = Math.round(
                      (week.completedDays / Math.max(week.overallDays, 1)) *
                        100,
                    );
                    const isCurrent = status === "notFinished";
                    const isFutureWeek =
                      !isProgramFinished && weekNumber > program.pointer_week;

                    return (
                      <div
                        key={week.title}
                        className="grid grid-cols-[34px_1fr] gap-3"
                      >
                        <div className="relative flex justify-center">
                          <div
                            className={`absolute top-10 h-[calc(100%_-_26px)] w-[2px] rounded-full ${
                              status === "finished"
                                ? "bg-[#DDFB1F]"
                                : "bg-[#E1E2DD]"
                            }`}
                          />
                          <div
                            className={`relative z-10 flex size-[34px] items-center justify-center rounded-full ${getWeekMarkerTone(status)}`}
                          >
                            <Zap
                              size={18}
                              fill="currentColor"
                              strokeWidth={2.1}
                            />
                          </div>
                        </div>

                        <div
                          className={`rounded-[22px] bg-white p-4 shadow-[0_12px_30px_rgba(24,24,24,0.045)] ring-1 ${
                            isCurrent
                              ? "ring-[#DDFB1F]/65"
                              : "ring-black/[0.025]"
                          }`}
                        >
                          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
                            <div className="min-w-0 pt-1">
                              <div
                                className={`text-[21px] font-black leading-none tracking-[0] ${getWeekTitleColor(status)}`}
                              >
                                {week.title}
                              </div>
                            </div>
                            <div className="flex shrink-0 items-start gap-2">
                              <div className="max-w-[150px] text-right">
                                <div
                                  className={`inline-flex max-w-full items-center rounded-[12px] px-2.5 py-1 text-[11px] font-black leading-none ${getWeekBadgeTone(
                                    status,
                                  )}`}
                                >
                                  <span className="truncate">
                                    {getWeekBadgeLabel(
                                      status,
                                      week.completedDays,
                                    )}
                                  </span>
                                </div>
                                {week.dateRangeLabel && (
                                  <div className="mt-1 text-[12px] font-bold leading-tight text-[#4F5458]">
                                    {week.dateRangeLabel}
                                  </div>
                                )}
                              </div>
                              <button
                                type="button"
                                aria-label={`${getWeekCaption(status, week.completedDays)}. ${week.title}`}
                                onClick={() => {
                                  if (isFutureWeek) {
                                    toast.message(FUTURE_WEEK_MESSAGE, {
                                      position: "top-center",
                                    });
                                    return;
                                  }

                                  nav.push(
                                    `/program/${enrollmentId}/week/${weekNumber}`,
                                  );
                                }}
                                className={`flex size-10 shrink-0 items-center justify-center rounded-[14px] transition-transform duration-150 ease-out active:scale-[0.96] ${
                                  isFutureWeek
                                    ? "bg-[#E9EAE6] text-[#9B9D97]"
                                    : "bg-[#F7FDE7] text-[#111214]"
                                }`}
                              >
                                <ChevronRight size={22} strokeWidth={2.2} />
                              </button>
                            </div>
                          </div>

                          <div className="mt-2.5 flex items-center gap-3">
                            <div className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-[#DADDE0]/80">
                              <div
                                className={`h-full rounded-full ${
                                  isCurrent ? "bg-[#D7FB36]" : "bg-[#7F86D3]"
                                }`}
                                style={{ width: `${weekProgress}%` }}
                              />
                            </div>
                            <div className="w-10 text-right text-[15px] font-semibold tabular-nums text-[#101113]">
                              {week.completedDays}/7
                            </div>
                          </div>

                          <div className="mt-3 grid grid-cols-4 gap-2">
                            {week.days.map((day) => (
                              <button
                                key={`${week.title}-${day.item.title}`}
                                type="button"
                                aria-label={`${week.title}, день ${day.item.title}`}
                                onClick={day.item.onClick}
                                className={`flex h-[56px] flex-col items-center justify-center rounded-[16px] text-[17px] font-black transition-transform duration-150 ease-out active:scale-[0.96] ${getDayTone(
                                  day.type,
                                  status,
                                )}`}
                              >
                                <span className="flex h-6 items-center justify-center leading-none">
                                  {day.type === "finished" ? (
                                    <Check size={22} strokeWidth={2.8} />
                                  ) : (
                                    day.item.title
                                  )}
                                </span>
                                {day.dateLabel && (
                                  <span className="mt-1 text-[9px] font-bold leading-none opacity-70">
                                    {day.dateLabel}
                                  </span>
                                )}
                              </button>
                            ))}
                            <div
                              className={`flex h-[56px] items-center justify-center rounded-[16px] ${
                                week.isCompleted
                                  ? "bg-[#DDFB1F] text-[#111111]"
                                  : "bg-[#F0F0EA] text-[#7A7A74]"
                              }`}
                            >
                              {week.isCompleted ? (
                                <Trophy size={23} strokeWidth={2.2} />
                              ) : (
                                <Trophy size={23} strokeWidth={2.2} />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div className="grid grid-cols-[34px_1fr] gap-3 pt-0">
                    <div className="relative flex justify-center">
                      <div
                        className={`relative z-10 flex size-[34px] items-center justify-center rounded-full ${
                          isProgramFinished
                            ? "bg-[#DDFB1F] text-[#111111] shadow-[0_10px_24px_rgba(221,251,31,0.24)]"
                            : "bg-[#E1E2DD] text-[#8D8F8A]"
                        }`}
                      >
                        <Trophy size={18} strokeWidth={2.2} />
                      </div>
                    </div>

                    <div className="rounded-[22px] bg-white p-4 shadow-[0_12px_30px_rgba(24,24,24,0.045)] ring-1 ring-black/[0.025]">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-[19px] font-black leading-none text-[#111111]">
                            Финишная прямая
                          </div>
                          <div className="mt-1 text-[12px] font-semibold leading-tight text-[#7A7A74]">
                            {finishCaption}
                          </div>
                        </div>
                        <div className="inline-flex h-9 shrink-0 items-center rounded-[14px] bg-[#F7FDE7] px-3 text-[13px] font-black tabular-nums text-[#111214]">
                          {progressPercentage}%
                        </div>
                      </div>
                      <div className="mt-3 h-2 overflow-hidden rounded-full bg-[#DADDE0]/80">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${progressPercentage}%`,
                            backgroundImage:
                              "repeating-linear-gradient(90deg, #DDFB1F 0 6px, #111111 6px 12px)",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </DefaultPage>
  );
};
